import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, Form, Button } from "react-bootstrap";
import Spinner from "../generics/Spinner";
import moment from "moment";

const MAINTENANCE_TYPE_OPTIONS = [
  { value: "SCHEDULED", label: "Scheduled Maintenance" },
  { value: "REPAIR", label: "Repair" },
  { value: "INSPECTION", label: "Inspection" },
  { value: "CLEANING", label: "Cleaning" },
  { value: "REPLACEMENT", label: "Part Replacement" },
  { value: "EMERGENCY", label: "Emergency Repair" },
  { value: "OTHER", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

const getMaintenanceTypeStyle = (type) => {
  const styles = {
    SCHEDULED: { backgroundColor: "#0d6efd", color: "#fff" },
    REPAIR: { backgroundColor: "#ffc107", color: "#000" },
    INSPECTION: { backgroundColor: "#0dcaf0", color: "#000" },
    CLEANING: { backgroundColor: "#6c757d", color: "#fff" },
    REPLACEMENT: { backgroundColor: "#343a40", color: "#fff" },
    EMERGENCY: { backgroundColor: "#ff5454", color: "#fff" },
    OTHER: { backgroundColor: "#6c757d", color: "#fff" },
  };
  return styles[type] || styles.OTHER;
};

const getRecordStatusStyle = (status) => {
  const styles = {
    SCHEDULED: { backgroundColor: "#0dcaf0", color: "#000" },
    IN_PROGRESS: { backgroundColor: "#ffc107", color: "#000" },
    COMPLETED: { backgroundColor: "#198754", color: "#fff" },
    CANCELLED: { backgroundColor: "#6c757d", color: "#fff" },
  };
  return styles[status] || styles.SCHEDULED;
};

const MaintenanceRecordsList = ({
  records,
  machines,
  loading,
  error,
  onFilter,
  onStart,
  onComplete,
  onCancel,
}) => {
  const [filters, setFilters] = useState({
    search: "",
    machine: "",
    status: "",
    maintenanceType: "",
    serviceProvider: "",
  });

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    if (onFilter) {
      onFilter(newFilters);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (onFilter) {
      onFilter(filters);
    }
  };

  const clearFilters = () => {
    const clearedFilters = {
      search: "",
      machine: "",
      status: "",
      maintenanceType: "",
      serviceProvider: "",
    };
    setFilters(clearedFilters);
    if (onFilter) {
      onFilter(clearedFilters);
    }
  };

  if (loading && records.length === 0) {
    return <Spinner />;
  }

  return (
    <div className="maintenance-records-list">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          <i className="fa fa-list me-2"></i>
          Maintenance Records
        </h4>
        <div>
          <Link to="/maintenance/records/new" className="btn btn-primary btn-sm me-1">
            <i className="fa fa-plus me-1"></i> Schedule
          </Link>
          <Link to="/maintenance/records/service-visit" className="btn btn-success btn-sm">
            <i className="fa fa-clipboard me-1"></i> Service Visit
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-header reports-filter">
          <Form onSubmit={handleSearch} className="row align-items-center mb-0">
            <div className="col-12 col-sm-3">
              <label className="col-form-label filter-label" htmlFor="recordSearch">
                <i className="fa fa-search mr-05 ml-05"></i>
                Search
              </label>
              <input
                type="text"
                id="recordSearch"
                className="form-control"
                placeholder="Title, description..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
            <div className="col-12 col-sm-2">
              <label className="col-form-label filter-label ml-05">
                Machine
              </label>
              <select
                className="form-control"
                value={filters.machine}
                onChange={(e) => handleFilterChange("machine", e.target.value)}
              >
                <option value="">All Machines</option>
                {machines.map((machine) => (
                  <option key={machine.id} value={machine.id}>
                    {machine.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2">
              <label className="col-form-label filter-label ml-05">
                Status
              </label>
              <select
                className="form-control"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="">All Status</option>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-3">
              <label className="col-form-label filter-label ml-05">
                Type
              </label>
              <select
                className="form-control"
                value={filters.maintenanceType}
                onChange={(e) => handleFilterChange("maintenanceType", e.target.value)}
              >
                <option value="">All Types</option>
                {MAINTENANCE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-2 d-flex align-items-end" style={{ paddingTop: "32px" }}>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearFilters}>
                <i className="fa fa-times me-1"></i> Clear
              </button>
            </div>
          </Form>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger py-2">
          <i className="fa fa-exclamation-circle me-2"></i>
          {error.message || "Error loading records"}
        </div>
      )}

      {/* Results */}
      <Card>
        <Card.Body className="p-0">
          {records.length === 0 ? (
            <div className="shipment-list--empty text-center py-5">
              <i className="fa fa-list fa-3x mb-3 text-muted"></i>
              <p className="mb-0 text-muted">No maintenance records found</p>
            </div>
          ) : (
            <table className="table table-striped table-bordered datatable mb-0">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Machine</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Provider</th>
                  <th>Cost</th>
                  <th className="text-center" style={{ width: "100px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr key={record.id}>
                    <td>
                      <Link to={`/maintenance/records/${record.id}`}>
                        <strong>{record.title}</strong>
                      </Link>
                      {record.description && (
                        <small className="d-block text-muted text-truncate" style={{ maxWidth: "180px" }}>
                          {record.description}
                        </small>
                      )}
                    </td>
                    <td>
                      <Link to={`/maintenance/machines/${record.machine}`}>
                        {record.machine_name}
                      </Link>
                      <br />
                      <small className="text-muted">{record.machine_location_name}</small>
                    </td>
                    <td>
                      <span className="badge badge-pill" style={getMaintenanceTypeStyle(record.maintenance_type)}>
                        {record.maintenance_type_display || record.maintenance_type}
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-pill" style={getRecordStatusStyle(record.status)}>
                        {record.status_display || record.status}
                      </span>
                    </td>
                    <td>
                      {record.completed_at ? (
                        <>
                          <small style={{ color: "#198754" }}>Completed</small>
                          <br />
                          <small className="text-muted">{moment(record.completed_at).format("MMM D, YYYY")}</small>
                        </>
                      ) : record.started_at ? (
                        <>
                          <small style={{ color: "#ffc107" }}>Started</small>
                          <br />
                          <small className="text-muted">{moment(record.started_at).format("MMM D, YYYY")}</small>
                        </>
                      ) : record.scheduled_date ? (
                        <>
                          <small style={{ color: "#0dcaf0" }}>Scheduled</small>
                          <br />
                          <small className="text-muted">{moment(record.scheduled_date).format("MMM D, YYYY")}</small>
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {record.service_provider_name || "-"}
                      {record.external_technician_name && (
                        <small className="d-block text-muted">
                          {record.external_technician_name}
                        </small>
                      )}
                    </td>
                    <td>
                      {record.cost ? `$${parseFloat(record.cost).toLocaleString()}` : "-"}
                    </td>
                    <td className="text-center">
                      <Link
                        to={`/maintenance/records/${record.id}`}
                        className="btn btn-sm btn-outline-primary me-1"
                        title="View"
                      >
                        <i className="fa fa-eye"></i>
                      </Link>
                      {record.status === "SCHEDULED" && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          className="me-1"
                          title="Start"
                          onClick={() => onStart && onStart(record.id)}
                        >
                          <i className="fa fa-play"></i>
                        </Button>
                      )}
                      {record.status === "IN_PROGRESS" && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-1"
                          title="Complete"
                          onClick={() => onComplete && onComplete(record.id)}
                        >
                          <i className="fa fa-check"></i>
                        </Button>
                      )}
                      {(record.status === "SCHEDULED" || record.status === "IN_PROGRESS") && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          title="Cancel"
                          onClick={() => onCancel && onCancel(record.id)}
                        >
                          <i className="fa fa-times"></i>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>

      {records.length > 0 && (
        <div className="text-muted small mt-2">
          Showing {records.length} record{records.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
};

export default MaintenanceRecordsList;
